import { each, filter, find, keys, map, partition, reduce } from "underscore";
import dayjs from "dayjs";
import {
    Channels,
    CommonWidgetMetrics,
    IW2DrilldownData,
    IW2DrilldownEntry,
    IW2FormattedDrilldownEntry,
    Platforms,
    RevstackFilterTypes,
} from "../common-types";
import {
    PIE_CHART_COLORS,
    REVSTACK_CHART_COLORS,
    REVSTACK_MEASURES,
    REVSTACK_TOOLTIP_PREFIXES_TO_REMOVE
} from "../constants";
import {
    capitalizeWords,
    getSortedChannelList,
    safeCloneArray,
    startCase,
    titleCase,
} from "../utils";
import {
    safeFormatNumber,
    getHeatmapColorStyle,
    getRevstackColors,
    formatMetric,
    getPreparedMetric,
    measuresToSkip,
    applyFilterConfig,
    getUniqueValues,
    generateSelectOptions,
    mergeUniqueByKey,
    getMappedDataKey,
    sortNumericData,
    removeDuplicatesByProperty,
    segregateMeasures,
} from "./helpers";
import { openPageInNewTab } from "./filters";
import store from "@/store";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";
import { RevstackDashboardModules } from "@/ts/common-types";

export const formatMainFilters = (val: any) => {
    const output: any = {
        categories: [],
        subcategories: [],
        brands: [],
        selected: {
            categories: [],
            subcategories: [],
            brands: [],
        },
    };

    const selectedCategoryIds = new Set();
    const selectedSubcategoryIds = new Set();
    const selectedBrandIds = new Set();
    const allBrandMap = new Map();

    for (const category of val) {
        // Add all categories
        output.categories.push({
            label: category.value,
            value: category.id,
        });

        const isSelectedCategory = category.is_most_relevant_category;

        if (isSelectedCategory) {
            output.selected.categories.push(category.id);
            selectedCategoryIds.add(category.id);
        }
        for (const subcat of category.subcategory_list || []) {
            // Add all subcategories
            output.subcategories.push({
                label: subcat.value,
                value: subcat.id,
                category: category.id,
            });

            if (isSelectedCategory) {
                selectedSubcategoryIds.add(subcat.id);
                output.selected.subcategories.push(subcat.id);
            }

            for (const brand of subcat.brand_list || []) {
                // Deduplicate based on brand.id
                if (!allBrandMap.has(brand.id)) {
                    allBrandMap.set(brand.id, {
                        label: brand.value,
                        value: brand.id,
                        subcategoryIds: new Set([subcat.id]),
                        brandType: brand.is_self_brand
                            ? "mybrand"
                            : "competition",
                    });
                } else {
                    allBrandMap.get(brand.id).subcategoryIds.add(subcat.id);
                }

                if (
                    isSelectedCategory &&
                    brand.is_self_brand &&
                    !selectedBrandIds.has(brand.id)
                ) {
                    selectedBrandIds.add(brand.id);
                    output.selected.brands.push(brand.id);
                }
            }
        }
    }

    // Convert allBrandMap to output.brands
    output.brands = Array.from(allBrandMap.values()).map((b: any) => ({
        label: b.label,
        value: b.value,
        subcategories: Array.from(b.subcategoryIds), // optional if you need it later
        brandType: b.brandType,
    }));

    return output;
};

const formatW2DrilldownData = (
    data: IW2DrilldownData,
    drilldownEntryKey = "sku",
    redirectOnValueClick?: boolean,
    redirectOnValueClickConfig?: {
        platformToOpenOnValueClick: Platforms,
        moduleToOpenOnValueClick: RevstackDashboardModules,
        type: string
    },
): IW2FormattedDrilldownEntry[] => {
    const resultMap: { [key: string]: IW2FormattedDrilldownEntry } = {};
    const orderTracker: string[] = [];
    let valueOnClick = null as any;
    Object.entries(data)?.forEach(([key, entries]: any) => {
        entries?.forEach((entry: IW2DrilldownEntry) => {
            let laggardField = entry[drilldownEntryKey];
            //!Note: We can move channel_id to config if more such mappings are requried...
            if (drilldownEntryKey === CommonWidgetMetrics.CHANNEL_ID) {
                laggardField = CHANNEL_CONFIGS[parseInt(laggardField)]?.name;
            }
            if (!laggardField) return;

            // Only include records with negative change numbers
            if (typeof entry.change === "number" && entry.change >= 0) return;

            if(redirectOnValueClick){
                if (redirectOnValueClickConfig?.moduleToOpenOnValueClick && redirectOnValueClickConfig?.platformToOpenOnValueClick) {
                    const mainFilters = store.getState().mainFilters.value;
                    valueOnClick = (() => openPageInNewTab({
                        platform: redirectOnValueClickConfig?.platformToOpenOnValueClick,
                        module: redirectOnValueClickConfig?.moduleToOpenOnValueClick,
                        type: redirectOnValueClickConfig?.type,
                        queryParams: { skus: [entry.sku], mainFilters, advancedFilters: {} },
                        mainFilters,
                    }))
                }
            }

            if (!resultMap[laggardField]) {
                resultMap[laggardField] = { laggardField, valueOnClick } as any;
                orderTracker.push(laggardField);
            }

            resultMap[laggardField][key] = {
                value: formatMetric(key, entry.value),
                change: formatMetric(key, entry.change),
                isPositive:
                    typeof entry.change === "number" && entry.change ? entry.change >= 0 : null,
                valueOnClick: valueOnClick,
            };
        });
    });

    return orderTracker.map((subcategory) => resultMap[subcategory]);
};

export const w0WidgetFormatter = (
    parentConfig: any,
    widget: any,
    widgetData: any
) => {

    const formatterParams = widget?.config?.banner?.formatterParams;
    const baseResultKey = widget?.queryMetaInfo?.resultKey;
    const percentData = widgetData[`${baseResultKey}_percent`];
    const nonPercentData = widgetData[`${baseResultKey}_non_percent`];
    const isCrossChannelComponent = formatterParams?.isCrossChannelComponent;
    const currencySymbol = widget?.config?.currencySymbol;
    let dataToFormat = [] as any;
    let aggregatedSkuChannelData = {} as any;

    const sortedChannels = getSortedChannelList();

    if (percentData && nonPercentData) {
        dataToFormat = mergePercentAndNonPercentData(
            percentData,
            nonPercentData,
            widget
        );
    }

    const rankedSkuChannelData = find(
        map(sortedChannels, (channel) =>
            find(
                dataToFormat,
                (item) => item.channel_id === channel.id.toString()
            )
        ),
        Boolean
    );

    const metricsToAvg = formatterParams?.metricsToAvg;
    const metricsToSum = formatterParams?.metricsToSum;

    if (isCrossChannelComponent) {
        // Pre-initialize accumulator to avoid repeated checks and loops
        const initialAcc: Record<string, number> = { count: 0 };

        // Initialize all metrics to 0 upfront
        if (metricsToAvg?.length) {
            metricsToAvg.forEach((metric: string) => {
                initialAcc[metric] = 0;
            });
        }
        if (metricsToSum?.length) {
            metricsToSum.forEach((metric: string) => {
                initialAcc[metric] = 0;
            });
        }

        aggregatedSkuChannelData = reduce(
            dataToFormat,
            (acc: Record<string, number>, item: any) => {
                acc.count += 1;

                // Process all metrics in a single loop for better performance
                const allMetrics = [...(metricsToAvg || []), ...(metricsToSum || [])];
                for (const metric of allMetrics) {
                    const value = item[metric];
                    // More efficient null/undefined check
                    if (value != null) {
                        const numValue = Number(value);
                        if (!isNaN(numValue)) {
                            acc[metric] += numValue;
                        }
                    }
                }

                return acc;
            },
            initialAcc
        );

        if (aggregatedSkuChannelData.count > 0 && metricsToAvg?.length) {
            for (const metric of metricsToAvg) {
                aggregatedSkuChannelData[metric] =
                    aggregatedSkuChannelData[metric] / aggregatedSkuChannelData.count;
            }
        }
    }

    const data = isCrossChannelComponent
        ? {
            image: rankedSkuChannelData?.thumbnail,
            sku: rankedSkuChannelData?.sku,
            title: rankedSkuChannelData?.sku_title,
            tags: rankedSkuChannelData?.category
                ? [
                    capitalizeWords(rankedSkuChannelData.category),
                    capitalizeWords(rankedSkuChannelData.brand),
                ]
                : [],
            rating: aggregatedSkuChannelData?.avg_rating_value?.toFixed(2),
            reviewCount: aggregatedSkuChannelData?.reviews_count, // This field is not available in the data
            currentPrice: `${currencySymbol} ${aggregatedSkuChannelData?.average_price_value?.toFixed(2)}`,
            originalPrice: `${currencySymbol} ${aggregatedSkuChannelData?.[
                    `${CommonWidgetMetrics.AVERAGE_MRP}_value`
                ]?.toFixed(2)
                }`,
            quantity: aggregatedSkuChannelData?.quantity, // This field is not available in the data
            product_url: rankedSkuChannelData?.product_url,
        }
        : {
            image: rankedSkuChannelData?.thumbnail,
            sku: rankedSkuChannelData?.sku,
            rating: rankedSkuChannelData?.avg_rating_value?.toFixed(2),
            reviewCount: rankedSkuChannelData?.reviews_count, // This field is not available in the data
            title: rankedSkuChannelData?.sku_title,
            tags: rankedSkuChannelData?.category
                ? [
                    capitalizeWords(rankedSkuChannelData.category),
                    capitalizeWords(rankedSkuChannelData.brand),
                ]
                : [],
            currentPrice: `${currencySymbol} ${rankedSkuChannelData?.average_price_value?.toFixed(2)}`,
            originalPrice: `${currencySymbol} ${rankedSkuChannelData?.[
                    `${CommonWidgetMetrics.AVERAGE_MRP}_value`
                ]?.toFixed(2)
                }`,
            quantity: rankedSkuChannelData?.quantity, // This field is not available in the data
            product_url: rankedSkuChannelData?.product_url,
        };

    return {
        data,
        isLoading: dataToFormat?.length === 0,
    };
};

export const w1WidgetFormatter = (widget: any, widgetData: any) => {

    const config = widget?.config

    const metricsData = reduce(
        widget.measures,
        (acc: any, measure: CommonWidgetMetrics) => {
            acc[measure] = {};
            each(widget.dimensions, (dimension: any) => {
                const preparedMetric = getPreparedMetric(measure);
                acc[measure][dimension] =
                    widgetData[`${preparedMetric}_${dimension}`];
                acc[measure].isMetricLoading =
                    !widgetData[`${preparedMetric}_value`];
                acc[measure].isGraphLoading =
                    !widgetData[`${preparedMetric}_series`];
                return acc;
            });
            return acc;
        },
        {}
    );
    const drillDownData = {} as any;
    if (config?.drilldown) {
        const formattedData = [] as any;

        const excludedMeasures =
            config.filterOptions
                ?.filter((option: any) => option.excludeAsRow === true)
                ?.map((option: any) => option.value) || [];

        each(widget.measures, (measure: any) => {
            const preparedMetric = getPreparedMetric(measure);
            drillDownData[measure] = widgetData[`${preparedMetric}_drilldown`];
            if (excludedMeasures.includes(preparedMetric)) {
                return;
            }

            const dimensionData = {} as any;
            each(widget.dimensions, (dimension: any) => {
                dimensionData[dimension] = widgetData[`${preparedMetric}_${dimension}`]
                    ? widgetData[`${preparedMetric}_${dimension}`]
                    : "-";
            });

            formattedData.push({
                name: REVSTACK_MEASURES[measure]?.label || measure,
                key: measure,
                ...dimensionData,
            });
        });

    }
    const data = {
        drilldownData: formatW2DrilldownData(
            drillDownData,
            config?.drilldownEntryKey
        ),
        data: metricsData,
        isDrilldownLoading:
            Object.values(drillDownData)?.filter(
                (value) => value !== undefined && value !== null
            )?.length === 0,
    }

    return data;
};

export const w2WidgetFormatter = (widget: any, widgetData: any) => {
    //TODO: get it from config, when this is being used in other modules also
    const statusCategories = {
        good: { id: "Good", color: "#198754" },
        medium: { id: "Moderate", color: "#efbe10" },
        poor: { id: "Poor", color: "#ff0000" },
    };
    const formattedData = [] as any;
    const config = widget?.config

    const excludedMeasures =
        config?.filterOptions
            ?.filter((option: any) => option.excludeAsRow === true)
            ?.map((option: any) => option.value) || [];

    const drillDownData = {} as any;

    each(widget.measures, (measure: any) => {
        drillDownData[measure] = widgetData[`${measure}_drilldown`];
        if (excludedMeasures.includes(measure)) {
            return;
        }

        const dimensionData = {} as any;
        each(widget.dimensions, (dimension: any) => {
            dimensionData[dimension] = widgetData[`${measure}_${dimension}`]
                ? widgetData[`${measure}_${dimension}`]
                : "-";
        });

        formattedData.push({
            name: REVSTACK_MEASURES[measure]?.label || measure,
            key: measure,
            ...dimensionData,
        });
    });

    const firstNonExcludedMeasure = widget.measures.find(
        (measure: any) => !excludedMeasures.includes(measure)
    );

    const positiveChanges = formattedData.filter((metric: any) => {
        const changeValue = metric.change;
        if (changeValue === undefined || changeValue === null) return false;
        if (typeof changeValue === "number" ) {
            if(metric?.name === REVSTACK_MEASURES[CommonWidgetMetrics.RELATIVE_PRICE_INDEX]?.label){
                return changeValue < 0;
            }
            return changeValue >= 0;
        }
    return false;
    }).length;

    const totalMetrics = formattedData.filter(
        (item: any) => !measuresToSkip.includes(item.key)
    ).length;
    const positivePercentage =
        totalMetrics > 0 ? (positiveChanges / totalMetrics) * 100 : 0;

    let status = statusCategories.poor.id;
    let statusColor = statusCategories.poor.color;

    if (positivePercentage === 100) {
        status = statusCategories.good.id;
        statusColor = statusCategories.good.color;
    } else if (positivePercentage >= 50) {
        status = statusCategories.medium.id;
        statusColor = statusCategories.medium.color;
    } else {
        status = statusCategories.poor.id;
        statusColor = statusCategories.poor.color;
    }


    const data = {
        drilldownData: formatW2DrilldownData(
            drillDownData,
            config.drilldownEntryKey,
            config?.drilldown?.redirectOnValueClick,
            config?.drilldown?.redirectOnValueClickConfig,
        ),
        metricsData: {
            status,
            statusColor,
            metrics: formattedData,
        },
        isMetricsLoading: firstNonExcludedMeasure
            ? !keys(widgetData).includes(`${firstNonExcludedMeasure}_value`)
            : false,
        isDrilldownLoading:
            Object.values(drillDownData)?.filter(
                (value) => value !== undefined && value !== null
            )?.length === 0,
    };

    return data;
};


const extractWidgetData = (
    widgetData: any,
    dataKey: string,
    baseResultKey: string
) => {
    const rawData = widgetData[dataKey];
    if (!rawData) return null;
    return rawData[baseResultKey] || rawData;
};

//TODO: Make things dynamic in this fn
export const w3WidgetFormatter = (
    parentConfig: any,
    widget: any,
    widgetData: any
) => {
    const { percMeasures, nonPercMeasures } = segregateMeasures(
        widget.measures as CommonWidgetMetrics[],
        widget.cubeId
    );

    const baseResultKey = widget?.queryMetaInfo?.resultKey;
    let dataToFormat = null;

    // Case 1: Only percent measures
    if (percMeasures.length && !nonPercMeasures.length) {
        dataToFormat = extractWidgetData(
            widgetData,
            `${baseResultKey}_percent`,
            baseResultKey
        );
    }
    // Case 2: Only non-percent measures
    else if (!percMeasures.length && nonPercMeasures.length) {
        dataToFormat = extractWidgetData(
            widgetData,
            `${baseResultKey}_non_percent`,
            baseResultKey
        );
    }
    // Case 3: Both percent and non-percent measures - need to merge
    else if (percMeasures.length && nonPercMeasures.length) {
        const percentData = extractWidgetData(
            widgetData,
            `${baseResultKey}_percent`,
            baseResultKey
        );
        const nonPercentData = extractWidgetData(
            widgetData,
            `${baseResultKey}_non_percent`,
            baseResultKey
        );

        // Wait until both datasets are available
        // If either is missing, dataToFormat stays null and widget shows loading state
        if (percentData && nonPercentData) {
            // Merge the time series data
            dataToFormat = {
                timeDimensionLabels:
                    percentData.timeDimensionLabels ||
                    nonPercentData.timeDimensionLabels,
                series: [
                    ...(percentData.series || []),
                    ...(nonPercentData.series || []),
                ],
            };
        }
    }

    const metricTypeLabel =
        store.getState().revstackLayoutFilters.value.metricType.selected ===
            "by_value"
            ? "Value"
            : "Volume";
    const componentData = {} as any;

    const parentFilters = parentConfig?.config?.filters;

    const timeSeriesData = dataToFormat;

    // If data isn't ready yet, return early with loading state
    if (!timeSeriesData) {
        componentData.isLoading = true;
        return { componentData, parentFilters };
    }
    const metricKey =
        widget?.config?.metricKey || widget?.queryMetaInfo?.primaryDimension;
    const formatForChannelId = widget?.config?.formatForChannelId;
    componentData.chartDataSeries = {
        categories: safeCloneArray(timeSeriesData.timeDimensionLabels).map(
            (date: string) => {
                // Check if the value is a valid date before formatting
                if (widget.config?.dateFormat && dayjs(date).isValid()) {
                    return dayjs(date).format(widget.config.dateFormat);
                }
                return date;
            }
        ),
        series: (timeSeriesData.series || []).map((seriesItem: any) => ({
            name: (() => {
                let name = seriesItem?.[metricKey] || "Unknown";

                // Check if name matches any key in REVSTACK_MEASURES and replace with label
                const nameLower = name.toLowerCase();
                const matchedMeasure = Object.entries(REVSTACK_MEASURES).find(
                    ([key]) => nameLower.includes(key.toLowerCase())
                );

                if (matchedMeasure) {
                    const [_, measure] = matchedMeasure;
                    name = measure.label;
                }
                return name;
            })(),
            data: safeCloneArray(seriesItem.data),
            unit: (() => {
                const metricValue = seriesItem?.[metricKey] || "unknown";
                return (
                    REVSTACK_MEASURES[metricValue]?.unit ||
                    REVSTACK_MEASURES[
                        metricValue.replace(/_by_(value|volume)_mrp/, "")
                    ]?.unit ||
                    ""
                );
            })(),
            ...(seriesItem[widget.queryMetaInfo.primaryDimension] && {
                [widget.queryMetaInfo.primaryDimension]: formatForChannelId
                    ? startCase(
                        Channels[
                        seriesItem[widget.queryMetaInfo.primaryDimension]
                        ]
                    )
                    : seriesItem[widget.queryMetaInfo.primaryDimension],
            }),
            ...widget.graphAttributes?.series?.format,
        })),
    };
    componentData.yAxisTitle =
        widget.config?.yAxisTitle || `Market Share% (By ${metricTypeLabel})`;
    componentData.yAxisUnit = widget.config?.yAxisUnit || "%";
    componentData.isLoading = false;

    if (parentFilters?.length) {
        const series = componentData.chartDataSeries.series;

        parentFilters.forEach((filter: any) => {
            if (filter.attributeKey) {
                // Extract and deduplicate in one step using Set
                const uniqueValues = new Set(
                    series.map((s: any) => s[filter.attributeKey])
                );

                // Convert to options array
                filter.filterOptions = Array.from(
                    uniqueValues,
                    (option: any) => {
                        const safeName =
                            typeof option === "string"
                                ? option
                                : String(option?.name ?? option);
                        return {
                            label: safeName,
                            value: safeName,
                        };
                    }
                );
            }
        });
    }

    return { componentData, parentFilters };
};

//TODO: Make things dynamic in this fn
export const w4WidgetFormatter = (widget: any, widgetData: any) => {
    const metricTypeLabel =
        store.getState().revstackLayoutFilters.value.metricType.selected ===
            "by_value"
            ? "Value"
            : "Volume";
    const formatForChannelId = widget?.config?.formatForChannelId;
    const isCategoryFormattingRequired = widget?.config?.isCategoryFormattingRequired ?? true;
    const componentData = {} as any;

    // Check if data has Time Dimension Labels data
    if (
        widgetData[widget?.queryMetaInfo?.resultKey]?.chartDataSeries
            ?.timeDimensionLabels
    ) {
        componentData.chartDataSeries = {
            categories: safeCloneArray(
                widgetData[widget?.queryMetaInfo?.resultKey]?.chartDataSeries
                    ?.timeDimensionLabels
            ),
            series: (
                widgetData[widget?.queryMetaInfo?.resultKey]?.chartDataSeries
                    ?.series || []
            ).map((seriesItem: any) => ({
                name: seriesItem.name || "Unknown",
                data: safeCloneArray(seriesItem.data),
            })),
        };

        componentData.subTitle = `By ${metricTypeLabel}`;
        componentData.yAxisTitle =
            widget.config?.yAxisTitle || `Market Share (By ${metricTypeLabel})`;
        componentData.yAxisUnit = widget.config?.yAxisUnit || "%";
        componentData.graphColors = getRevstackColors(
            widget.config?.graphColors || REVSTACK_CHART_COLORS
        );
        componentData.isLoading = false;

        return componentData;
    }

    const barChartData = widgetData?.[widget?.queryMetaInfo?.resultKey];
    const primaryWidgetDimension = widget?.queryMetaInfo?.primaryDimension;
    const hasMultipleDimensions = widget.queryDimensions?.length > 1;

    if (barChartData && barChartData[primaryWidgetDimension] && barChartData.series) {
        componentData.chartDataSeries = {
            categories: hasMultipleDimensions ? 
                Object.assign({}, ...widget.queryDimensions.map((dimension: any) => ({
                    [dimension]: safeCloneArray(barChartData[dimension])
                })))
            : safeCloneArray(
                map(barChartData[primaryWidgetDimension], (item: any) =>
                    formatForChannelId ? titleCase(Channels[item]) : isCategoryFormattingRequired ? capitalizeWords(item) : item
                )
            ),
            series: (barChartData.series || []).map((seriesItem: any) => ({
                name: seriesItem.name || "Unknown",
                data: safeCloneArray(seriesItem.data),
            })),
        };

        componentData.subTitle = `By ${metricTypeLabel}`;
        componentData.yAxisTitle = widget.config?.yAxisTitle || `Market Share (By ${metricTypeLabel})`;
        componentData.yAxisUnit = widget.config?.yAxisUnit || "%";
        componentData.graphColors = getRevstackColors(widget.config?.graphColors || REVSTACK_CHART_COLORS);
    }

    componentData.isLoading =
        !barChartData || !barChartData[primaryWidgetDimension] || !barChartData.series;
    return componentData;
};


const formatTooltipData = (tooltipData: any): Array<{ key: string, value: any, change: any }> => {
    if (!tooltipData) {
        return [];
    }

    // Handle both array format and direct object format
    const data = Array.isArray(tooltipData) ? tooltipData[0] : tooltipData;
    return Object.keys(data).reduce<Array<{ key: string, value: any, change: any }>>((formattedData, key) => {
        const item = data[key];
        if (item && typeof item === 'object' && item.value !== undefined) {
            // Remove any of the specified prefixes if they exist
            let cleanKey = key;
            for (const prefix of REVSTACK_TOOLTIP_PREFIXES_TO_REMOVE) {
                if (cleanKey.startsWith(prefix)) {
                    cleanKey = cleanKey.replace(prefix, '');
                    break; // Only remove the first matching prefix
                }
            }
            formattedData.push({
                key: titleCase(cleanKey),
                value: item.value,
                change: item.change
            });
        }
        return formattedData;
    }, []);
};

export const w5WidgetFormatter = (widget: any, widgetData: any) => {
    const componentData = {} as any;
    const pieChartData = widgetData?.[widget?.queryMetaInfo?.resultKey] || {};
    const tooltipDataKey = widget?.queryMetaInfo?.tooltipKey || {};

    if (pieChartData?.chartDataSeries) {
        componentData.chartDataSeries = map(
            pieChartData.chartDataSeries,
            (item: any) => ({
                name: item[widget?.queryMetaInfo?.primaryDimension]
                    ? capitalizeWords(
                        item[widget?.queryMetaInfo?.primaryDimension]
                    )
                    : "-",
                value: safeFormatNumber(item.value),
                change: safeFormatNumber(item.change),
                ...(item[tooltipDataKey] && {
                    [tooltipDataKey]: formatTooltipData(item[tooltipDataKey])
                })
            })
        );
        componentData.subTitle = pieChartData.subTitle;
        componentData.yAxisTitle = pieChartData.yAxisTitle;
        componentData.yAxisUnit = pieChartData.yAxisUnit;
        //TODO: Temp commented have to restore it once calc is sorted DND
        // componentData.selectedBrandsRank = pieChartData.selectedBrandsRank;
        componentData.graphColors = PIE_CHART_COLORS;
    }

    if (pieChartData.selectedBrands) {
        componentData.selectedBrands = map(pieChartData.selectedBrands, (item: any) => ({
            name: capitalizeWords(item.brand),
            value: safeFormatNumber(item.value),
            change: safeFormatNumber(item.change),
            isPositive: item?.change ? item?.change >= 0 : null,
        }));
    }

    componentData.isLoading = !pieChartData || !pieChartData.chartDataSeries;
    return componentData;
};

export const w6WidgetFormatter = (
    widget: any,
    widgetData: any
): any => {

    const resultKey = widget?.queryMetaInfo?.resultKey;
    const data = widgetData[resultKey];

    if (!data) {
        return { cardsData: [], topCardData: {}, isLoading: true };
    }

    const dataKeyMap = widget?.config?.dataKeyMap;
    const cardType = widget?.config?.card?.cardType;
    const nameKey = keys(dataKeyMap).includes(cardType) ? dataKeyMap[cardType] : cardType;
    const idKey = widget?.config?.card?.cardId;
    const primaryMetricKey = dataKeyMap?.[widget?.config?.card?.primaryMetricKey];
    const secondaryMetricKey = dataKeyMap?.[widget?.config?.card?.secondaryMetricKey];

    const allChanges = map(
        data,
        (item) => item[primaryMetricKey]?.change || 0
    );
    const minChange = Math.min(...allChanges);
    const maxChange = Math.max(...allChanges);
    const sortKey = widget?.config?.card?.primaryMetricKey;

    const cardsData = map(data, (item) => {

        const preparedPrimaryKey = getPreparedMetric(primaryMetricKey);
        const preparedSecondaryKey = getPreparedMetric(secondaryMetricKey);
        const primaryMetric = item[preparedPrimaryKey];
        const secondaryMetric = item[preparedSecondaryKey];

        const primaryChange = primaryMetric?.change || 0;
        const secondaryChange = secondaryMetric?.change || 0;

        const result = {
            name: capitalizeWords(item[nameKey]),
            [idKey]: item[idKey],
            [widget?.config?.card?.primaryMetricKey]: {
                value: primaryMetric?.value,
                trend: { isPositive: primaryChange ? primaryChange >= 0 : null, value: primaryChange || "-" },
            },
            [widget?.config?.card?.secondaryMetricKey]: {
                value: secondaryMetric?.value,
                trend: {
                    isPositive: secondaryChange ? secondaryChange >= 0 : null,
                    value: secondaryChange || "-",
                },
            },
            color: getHeatmapColorStyle(primaryChange, minChange, maxChange),
        };



        return result;
    }).sort(
        (a, b) => (a[sortKey]?.trend?.value || 0) - (b[sortKey]?.trend?.value || 0)
    );

    //!Note: This is with the assumption that topcard will always have a metric thats already avl in the layout
    const topCard = widget?.config?.card?.topCard;
    const topCardPmKey = getPreparedMetric(topCard?.primaryMetricKey);
    const topCardSmKey = getPreparedMetric(topCard?.secondaryMetricKey);
    const topCardData = {
        name: topCard?.name,
        [widget?.config?.card?.primaryMetricKey]: {
            value: widgetData[`${topCardPmKey}_value`] || 0,
            trend: {
                isPositive: widgetData[`${topCardPmKey}_change`] ? widgetData[`${topCardPmKey}_change`] > 0 : null,
                value: widgetData[`${topCardPmKey}_change`] || "-",
            },
        },
        color: topCard?.color,
        id: topCard?.id,
    };

    if (topCardSmKey) {
        topCardData[widget?.config?.card?.secondaryMetricKey] = {
            value: widgetData[`${topCardSmKey}_value`] || "-",
            trend: {
                isPositive: widgetData[`${topCardSmKey}_change`] ? widgetData[`${topCardSmKey}_change`] > 0 : null,
                value: widgetData[`${topCardSmKey}_change`] || "-",
            },
        };
    }

    return { cardsData, topCardData, isLoading: false };
};

const mergePercentAndNonPercentData = (
    percentData: any,
    nonPercentData: any,
    widget: any,
    baseFilterKey = '',
    isExpandedData = false
) => {
    const nonPercentMap: { [key: string]: any } = {};

    const mergeKeys = isExpandedData ? widget?.expandRowQueryConfig?.queryMetaInfo?.recordKey?.split('##') : widget?.queryMetaInfo?.recordKey?.split('##');
    each(nonPercentData, (item: any) => {
        if (mergeKeys.length > 0) {
            // Create composite key from individual merge key values
            const compositeKey = mergeKeys
                .map((key: string) => item?.[key] || '')
                .join('##');
            nonPercentMap[compositeKey] = item;
        }
    });

    const sortedPercentData = baseFilterKey
        ? [...percentData].sort((a: any, b: any) => {
            const aValue = a[baseFilterKey] || 0;
            const bValue = b[baseFilterKey] || 0;
            return aValue - bValue; // Sort in ascending order
        })
        : percentData;

    const mergedData = map(sortedPercentData, (percentItem: any) => {
        let nonPercentItem = {};
        if (mergeKeys.length > 0) {
            // Create composite key for lookup
            const lookupKey = mergeKeys
                .map((key: string) => percentItem?.[key] || '')
                .join('##');
            nonPercentItem = nonPercentMap[lookupKey] || {};
        }
        return { ...percentItem, ...nonPercentItem };
    });
    return mergedData;
};

const processTableFilters = (filters: any[], widget: any, tableData: any) => {
    if (!filters || filters.length === 0) return [];

    return filters.map((filter: any) => {
        const processedFilter = { ...filter };

        if (filter.type === RevstackFilterTypes.SELECT && filter.dataKey) {
            processedFilter.mappedDataKey = getMappedDataKey(filter, widget, "table");

            const uniqueValues = getUniqueValues(tableData, filter.dataKey);
            const dynamicOptions = generateSelectOptions(uniqueValues);
            const existingOptions = filter.filterOptions || [];

            processedFilter.filterOptions = mergeUniqueByKey(existingOptions, dynamicOptions, "value");
        }

        return processedFilter;
    });
};

const w7WidgetTableFormatter = (
    widget: any,
    dataKeyMap: any,
    dataToFormat: any
) => {
    let tableData = {} as any;
    // Helper function to process data items
    const processDataItems = (dataItems: any[]) => {
        const td: any[] = [];
        const drilldownDataKeyMap =
            widget?.config?.table?.drilldown?.dataKeyMap;

        each(dataItems, (data) => {
            const tableBaseData: any = {};

            each(keys(dataKeyMap), (mapKey) => {
                const value = data?.[dataKeyMap?.[mapKey]];
                tableBaseData[mapKey] = value;
            });

            if (drilldownDataKeyMap) {
                const anomalies: any[] = [];
                const drilldown: any[] = [];

                const maxMetrics = Math.max(
                    ...map(
                        keys(drilldownDataKeyMap),
                        (drillKey) => drilldownDataKeyMap[drillKey].length
                    )
                );

                const drilldownMap = new Map();

                each(keys(drilldownDataKeyMap), (drillKey) => {
                    const metrics = drilldownDataKeyMap[drillKey];
                    const anomalyMetrics: any[] = [];

                    // Process metrics for this category
                    each(metrics, (metricConfig, index) => {
                        const change =
                            data[
                            `${getPreparedMetric(
                                metricConfig.measure
                            )}_change`
                            ] || 0;
                        const value =
                            data[
                            `${getPreparedMetric(
                                metricConfig.measure
                            )}_value`
                            ] || 0;
                        const formattedValue = formatMetric(
                            metricConfig.measure,
                            value
                        );
                        const formattedChange = formatMetric(
                            metricConfig.measure,
                            change
                        );

                        if (!drilldownMap.has(index)) {
                            drilldownMap.set(index, {});
                        }

                        drilldownMap.get(index)[drillKey] = {
                            metric: metricConfig.measure,
                            label: metricConfig.name,
                            value: value || "-",
                            change: change || "-",
                            isPositive: change ? change >= 0 : null,
                        };

                        if (change < 0) {
                            anomalyMetrics.push({
                                name: metricConfig.name,
                                value: formattedValue || "-",
                                change: formattedChange || "-",
                                isPositive: change ? +change >= 0 : null,
                            });
                        }
                    });

                    if (anomalyMetrics.length > 0) {
                        anomalies.push({
                            title: titleCase(drillKey),
                            metrics: anomalyMetrics,
                        });
                    }
                });

                for (let i = 0; i < maxMetrics; i++) {
                    const drilldownItem = drilldownMap.get(i) || {};

                    each(keys(drilldownDataKeyMap), (drillKey) => {
                        if (!drilldownItem[drillKey]) {
                            drilldownItem[drillKey] = { label: "", value: "" };
                        }
                    });

                    drilldown.push(drilldownItem);
                }

                tableBaseData.anomalies = anomalies;
                tableBaseData.drilldown = drilldown;
            }

            td.push({ ...tableBaseData, ...data, drilldown_action: true });
        });

        return td;
    };

    if (Array.isArray(dataToFormat)) {
        tableData = processDataItems(dataToFormat);
    } else {
        keys(dataToFormat).forEach((key) => {
            tableData[key] = processDataItems(dataToFormat[key]);
        });
    }
    return tableData;
};

const w7WidgetGroupedConsolidatedTableFormatter = (formatterParams: any, dataToFormat: any, isExpandable = false) => {
    //TODO: Grouping for non consolidated data also have to be handled in this, once we have a similar view or migrate drilldown table to this we can add that condition
    /**
     * ! Note: Currently we are handling the consolidated data condition only
     * Consolidated data will be like `ALL Channels` etc and will be stored in the consolidationDataKey
     */
    
    const drilldownFilters = store.getState().drilldownFilters.value;
    const consolidationDataDDFilters = formatterParams?.isDrilldownTable ? (drilldownFilters as any)?.consolidationDataFilters : [];
    const consolidationDataFilters = isExpandable ? formatterParams?.expandedConsolidationDataFilters : [...consolidationDataDDFilters, ...formatterParams?.consolidationDataFilters ? formatterParams?.consolidationDataFilters : []];

    // Get the appropriate group data parameters based on expandable state
    const currentGroupDataParams = isExpandable ? formatterParams?.expandedGroupDataParams : formatterParams?.groupDataParams;

    const groupedByMetric = dataToFormat.reduce((acc: any, item: any) => {
        // Create a composite key from all group parameters
        const metricKeyParts: string[] = [];
        const metricData: any = {};
        
        // Loop through all group data parameters to build the key and data
        currentGroupDataParams?.forEach((param: any) => {
            const keyValue = item[param.groupDataKey];
            const idValue = item[param.groupDataId];
            // Include both the key value and ID value in the composite key to ensure unique grouping
            metricKeyParts.push(`${param.groupDataKey}:${keyValue}|${param.groupDataId}:${idValue}`);
            metricData[param.groupDataKey] = keyValue;
            metricData[param.groupDataId] = idValue;
        });
        
        const metricKey = metricKeyParts.join('||'); // Use || as separator for composite key parts

        if (!acc[metricKey]) {
            acc[metricKey] = {
                ...metricData,
                [formatterParams?.consolidationDataKey]: {},
                [formatterParams?.consolidationAggregateDataKey]: {}
            };
        }

        const consolidationDataId = item?.[formatterParams?.consolidationDataId];
        if (consolidationDataId !== formatterParams?.consolidateDataIdentifier) {
            // Skip aggregate row (channel_id: 0)
            // Store channel-specific data with all API fields
            acc[metricKey][formatterParams?.consolidationDataKey][consolidationDataId] = { ...item };
        } else {
            // Store aggregate data (ALL CHANNELS) with all API fields
            acc[metricKey][formatterParams?.consolidationAggregateDataKey] = { ...item };
        }

        return acc;
    }, {});

    // Convert to array format expected by the table
    const formattedData = Object.keys(groupedByMetric).map((categoryKey, index) => {
        const categoryData = groupedByMetric[categoryKey];
        
        // Build the result object with all group data keys and IDs
        const resultObject: any = {
            key: (index + 1).toString(),
            ...categoryData[formatterParams?.consolidationAggregateDataKey],
            ...categoryData[formatterParams?.consolidationDataKey]
        };
        
        // Add all group data keys and IDs from the current parameters
        currentGroupDataParams?.forEach((param: any) => {
            resultObject[param.groupDataKey] = categoryData[param.groupDataKey];
            resultObject[param.groupDataId] = categoryData[param.groupDataId];
        });

        return resultObject;
    });
    const filteredConsolidatedData = consolidationDataFilters && Array.isArray(consolidationDataFilters) && consolidationDataFilters.length > 0
        ? consolidationDataFilters.reduce((data, filter) => applyFilterConfig(data, filter), formattedData)
        : formattedData;

    return filteredConsolidatedData;
}

export const w7WidgetGroupedTableFormatter = (widget: any, widgetData: any) => {
    const baseResultKey = widget?.queryMetaInfo?.resultKey;
    const recordKey = widget?.queryMetaInfo?.recordKey;
    const percentData = widgetData[`${baseResultKey}_percent`];
    const nonPercentData = widgetData[`${baseResultKey}_non_percent`];
    let dataToFormat = [] as any;

    if (percentData && nonPercentData) {
        // Create a map of nonPercentData by record_key for efficient lookup
        const nonPercentMap: { [key: string]: any } = {};
        each(nonPercentData, (item: any) => {
            if (item?.[recordKey]) {
                nonPercentMap[item[recordKey]] = item;
            }
        });

        // Combine the arrays based on record_key
        dataToFormat = map(percentData, (percentItem: any) => {
            const nonPercentItem = nonPercentMap[percentItem[recordKey]] || {};
            return { ...percentItem, ...nonPercentItem };
        });

        if (!dataToFormat) return { tableData: [] as any, isLoading: true };
    }

    return { tableData: dataToFormat, isLoading: !keys(dataToFormat).length };
};

//TODO: We have to eliminate the `dataKeyMap` from the config, its better if we can leverage the original keys only
export const w7WidgetFormatter = (
    parentConfig: any,
    widget: any,
    widgetData: any
) => {
    const baseResultKey = widget?.queryMetaInfo?.resultKey;
    const isExpandable = widget?.config?.table?.isExpandable || false;
    const consolidatedResultKey =
        widget?.queryMetaInfo?.consolidatedQueryConfig?.resultKey;
    const percentData = widgetData[`${baseResultKey}_percent`];
    const nonPercentData = widgetData[`${baseResultKey}_non_percent`];
    const consolidatedPercentData = consolidatedResultKey
        ? widgetData[`${consolidatedResultKey}_percent`]
        : undefined;
    const consolidatedNonPercentData = consolidatedResultKey
        ? widgetData[`${consolidatedResultKey}_non_percent`]
        : undefined;

    const expandRowQueryConfig = widget?.expandRowQueryConfig;
    const expandedBaseResultKey = expandRowQueryConfig?.queryMetaInfo?.resultKey;
    const expandedConsolidatedResultKey = expandRowQueryConfig?.queryMetaInfo?.consolidatedQueryConfig?.resultKey;
    const expandedPercentData = widgetData[`${expandedBaseResultKey}_percent`];
    const expandedNonPercentData = widgetData[`${expandedBaseResultKey}_non_percent`];
    const expandedConsolidatedPercentData = expandedConsolidatedResultKey
        ? widgetData[`${expandedConsolidatedResultKey}_percent`]
        : undefined;
    const expandedConsolidatedNonPercentData = expandedConsolidatedResultKey
        ? widgetData[`${expandedConsolidatedResultKey}_non_percent`]
        : undefined;

    const dataKeyMap = widget?.config?.table?.dataKeyMap;
    const parentFilters = parentConfig?.config?.filters;
    const tableFilters = widget?.config?.table?.filters;
    const isDrilldownTable = widget?.config?.table?.isDrilldownTable;
    const isGroupedTable = widget?.config?.table?.isGroupedTable;
    const isGroupedConsolidatedTable =
        consolidatedPercentData && consolidatedNonPercentData;
    const formatterParams = widget?.config?.table?.formatterParams;
    const componentData = {} as any;
    let dataToFormat = [] as any;
    let expandedDataToFormat = [] as any;
    let tableData = [] as any;
    let expandedTableData = [] as any;
    //TODO: Refactor this part (DRY)
    if ((isDrilldownTable || isGroupedTable) && !isGroupedConsolidatedTable) {
        return w7WidgetGroupedTableFormatter(widget, widgetData);
    }

    if (percentData && nonPercentData) {
        const baseFilterKey = widget?.queryMetaInfo?.baseFilter;

        dataToFormat = mergePercentAndNonPercentData(
            percentData,
            nonPercentData,
            widget,
            baseFilterKey
        );

        if (!dataToFormat) return { tableData: [] as any, isLoading: true };

        if (baseFilterKey) {
            const sortKey = widget?.queryMetaInfo?.sortConfig?.sortKey;
            const limit = widget?.queryMetaInfo?.sortConfig?.limit || 5;

            //[DND] SOV filtering > 1 for subcategories
            const [strongData, weakData] = partition(
                dataToFormat,
                (item) => (item[baseFilterKey] || 0) > 1
            );

            const getTopItems = (
                strong: any[],
                weak: any[],
                config: any,
                ascending: boolean
            ) => {
                const filteredStrong = config
                    ? applyFilterConfig(strong, config)
                    : strong;
                const filteredWeak = config
                    ? applyFilterConfig(weak, config)
                    : weak;

                const sortFn = (a: any, b: any) => {
                    const aVal = Number(a[sortKey]) ?? -Infinity;
                    const bVal = Number(b[sortKey]) ?? -Infinity;
                    return ascending ? aVal - bVal : bVal - aVal;
                };

                let combined = [...filteredStrong];

                if (combined.length < limit && filteredWeak.length > 0) {
                    const filler = filteredWeak.slice(
                        0,
                        limit - combined.length
                    );
                    combined = combined.concat(filler);
                }

                return combined.sort(sortFn).slice(0, limit); // final sort after filling
            };

            // Check if filterOptions are defined in config
            // Check if we have filters defined
            if (tableFilters && tableFilters.length > 0) {
                // Check for different filter types
                const radioFilter = tableFilters.find(
                    (filter: any) => filter.type === RevstackFilterTypes.RADIO
                );
                const dropdownFilter = tableFilters.find(
                    (filter: any) => filter.type === RevstackFilterTypes.SELECT
                );

                if (radioFilter && radioFilter.filterOptions) {
                    // Handle radio filters with segregated data
                    const segregatedData: any = {};

                    radioFilter.filterOptions.forEach((option: any) => {
                        const { value, filterConfig, sortOrder } = option;
                        const ascending = sortOrder === "asc";

                        if (filterConfig) {
                            // Apply custom filter configuration
                            segregatedData[value] = getTopItems(
                                strongData,
                                weakData,
                                filterConfig,
                                ascending
                            );
                        } else {
                            // No filtering config, return all data (sorted if sortOrder specified)
                            let allData = [...strongData, ...weakData];
                            if (sortKey && sortOrder) {
                                allData = sortNumericData(
                                    allData,
                                    sortKey,
                                    ascending
                                );
                            }
                            segregatedData[value] = allData;
                        }
                    });

                    dataToFormat = segregatedData;
                } else if (dropdownFilter) {
                    let allData = [...strongData, ...weakData];

                    if (sortKey) {
                        allData = sortNumericData(allData, sortKey, false);
                    }
                    dataToFormat = allData;
                } else {
                    dataToFormat = [...strongData, ...weakData];
                }
            } else {
                dataToFormat = [...strongData, ...weakData];
            }
        }
    }

    if (isExpandable && expandedPercentData && expandedNonPercentData) {
        expandedDataToFormat = mergePercentAndNonPercentData(
            expandedPercentData,
            expandedNonPercentData,
            widget,
            expandedBaseResultKey,
            isExpandable
        );

    }

    // Helper function to merge consolidated data
    const mergeConsolidatedData = (
        percentData: any,
        nonPercentData: any,
        resultKey: string,
        consolidatedColumnValue: number,
        isExpanded = false
    ) => {
        return map(
            mergePercentAndNonPercentData(
                percentData,
                nonPercentData,
                widget,
                resultKey,
                isExpanded
            ),
            (item: any) => ({
                ...item,
                [widget?.config?.table?.formatterParams?.consolidationDataId]:
                    consolidatedColumnValue || 0,
            })
        );
    };

    if (consolidatedPercentData && consolidatedNonPercentData) {
        const consolidatedData = mergeConsolidatedData(
            consolidatedPercentData,
            consolidatedNonPercentData,
            undefined,
            widget?.queryMetaInfo?.consolidatedQueryConfig?.consolidatedColumnValue
        );

        dataToFormat = [...dataToFormat, ...consolidatedData];
    }

    if (isExpandable && expandedConsolidatedPercentData && expandedConsolidatedNonPercentData) {
        const expandedData = mergeConsolidatedData(
            expandedConsolidatedPercentData,
            expandedConsolidatedNonPercentData,
            expandedConsolidatedResultKey,
            expandRowQueryConfig?.queryMetaInfo?.consolidatedQueryConfig?.consolidatedColumnValue,
            true
        );
        expandedDataToFormat = [...expandedDataToFormat, ...expandedData];
    }

    // Helper function to format table data
    const formatTableData = (data: any, isExpanded = false) => {
        return isGroupedConsolidatedTable
            ? w7WidgetGroupedConsolidatedTableFormatter(
                formatterParams,
                data,
                isExpanded
            )
            : w7WidgetTableFormatter(widget, dataKeyMap, data);
    };

    tableData = formatTableData(dataToFormat);
    expandedTableData = formatTableData(expandedDataToFormat, true);

    const hasActualData = Array.isArray(tableData)
        ? tableData?.length > 0
        : keys(tableData).some(
            (key) =>
                Array.isArray(tableData[key]) && tableData[key].length > 0
        );

    if (isExpandable && expandedDataToFormat) {
        componentData.expandedTableData = expandedTableData;
    }

    componentData.tableData = tableData;
    componentData.isLoading = !hasActualData;

    // Process table filters
    if (tableFilters && tableFilters.length > 0) {
        componentData.childFilters = processTableFilters(
            tableFilters,
            widget,
            tableData
        );
    }

    // Process parent filters
    if (parentFilters && parentFilters.length > 0) {
        componentData.parentFilters = processTableFilters(
            parentFilters,
            widget,
            tableData
        );
    }

    return componentData;
};

export const w9WidgetFormatter = (parentConfig: any, widget: any, widgetData: any) => {
    const parentFilters = parentConfig?.config?.filters;
    const queryMetaInfo = widget?.queryMetaInfo;
    const resultKey = queryMetaInfo?.resultKey;
    const recordKey = queryMetaInfo?.recordKey;
    const consolidatedResultKey = queryMetaInfo?.consolidatedResultKey;
    const consolidatePercentData = widgetData[`${consolidatedResultKey}_percent`];
    const consolidateNonPercentData = widgetData[`${consolidatedResultKey}_non_percent`];
    const consolidatedData = {
        ...consolidatePercentData,
        ...consolidateNonPercentData,
    } as any;

    const percentData = widgetData[`${resultKey}_percent`];
    const nonPercentData = widgetData[`${resultKey}_non_percent`];
    let dataToFormat = [] as any;

    if (percentData && nonPercentData) {
        const nonPercentMap: { [key: string]: any } = {};
        each(nonPercentData, (item: any) => {
            if (item?.[recordKey]) {
                nonPercentMap[item[recordKey]] = item;
            }
        });

        dataToFormat = map(percentData, (percentItem: any) => {
            const nonPercentItem = nonPercentMap[percentItem[recordKey]] || {};
            return { ...percentItem, ...nonPercentItem };
        });

        if (!dataToFormat) return { data: [] as any, isLoading: true };
    }

    let processedParentFilters = parentFilters;
    if (parentFilters && parentFilters.length > 0 && parentFilters[0]) {
        processedParentFilters = structuredClone(parentFilters);
        //TODO: When filter types are changed based on view we can fig out a way to get filters for it from config
        const categoryList = store.getState().mainFiltersData.value.categories;
        const selectedCategories = store.getState().mainFilters.value.categories;
        const defaultFilterOptions = processedParentFilters[0].filterOptions || [];
        const freshFilterOptions = filter(categoryList, (filter: any) => {
            return selectedCategories.includes(filter.value);
        });
        const combinedOptions = [...defaultFilterOptions, ...freshFilterOptions];
        processedParentFilters[0].filterOptions = removeDuplicatesByProperty(combinedOptions, 'value');
    }

    const allChannels = store.getState().revstackChannelsList.value;
    const channelImageMap = allChannels.reduce((acc: any, channel: any) => {
        acc[channel.id] = channel?.logo_url || channel?.alias;
        return acc;
    }, {});

    const viewOrderMap = new Map(allChannels.map(c => [c.id, c.view_order]));

    const sortedDataToFormat = [...dataToFormat].sort(
        (a, b) =>
            (viewOrderMap.get(+a.channel_id)) -
            (viewOrderMap.get(+b.channel_id))
    );

    const data = [
        ...(keys(consolidatedData)?.length > 0 ? [{
            id: 0,
            name: 'ALL CHANNELS',
            icon: '',
            ...consolidatedData
        }] : []),
        ...(sortedDataToFormat?.length > 0 ? sortedDataToFormat.map((item: any) => {
            return {
                ...item,
                icon: channelImageMap[item.channel_id],
                id: item.channel_id,
                name: CHANNEL_CONFIGS[item.channel_id]?.name
            }
        }) : [])
    ];

    const hasDataToFormat = sortedDataToFormat?.length > 0;
    const hasConsolidatedData = keys(consolidatedData)?.length > 0;

    return {
        data,
        isLoading: !hasDataToFormat && !hasConsolidatedData,
        parentFilters: processedParentFilters
    };
};